/**
 * Market tags (#875): the bundled + downloadable rules, the remembered chip selection and the catalog keys of tag names.
 * Algorithm and chip helpers: ./classify.ts. Behaviour: docs/research/CLASSIFICATION.md "3b. App behaviour".
 */
import { questionIndex, type MetaLike } from '../describe/describe';
import type { PlainKey } from '../i18n/i18n';
import { classify, loadRules, recordForOutcome, recordForQuestion, tagKey, type Network, type Rules, type RulesData } from './classify';
import bundled from './rules.generated.json';

export * from './classify';

/** The rules shipped with this build (shared/classify/tags.json via shared/classify/tools/gen.py). */
export const RULES: Rules = loadRules(bundled as unknown as RulesData);

/** Catalog key of a tag's display name (every tag has one: gen.py fails otherwise). */
export function tagLabelKey(id: string): PlainKey {
  return tagKey(id) as PlainKey;
}

export interface MetaTags {
  /** Outcome id -> tags, classified from [its question, the outcome]. */
  outcome: ReadonlyMap<number, string[]>;
  /** Question id -> tags, classified from the question alone (question cards). */
  question: ReadonlyMap<number, string[]>;
}

const metaCache = new WeakMap<object, { network: Network; rules: Rules; tags: MetaTags }>();

/** Tags of every outcome and question in an outcomeMeta, memoized per meta object (and network, rules). */
export function classifyMeta(meta: MetaLike | null | undefined, network: Network, rules: Rules = RULES): MetaTags {
  if (!meta) return { outcome: new Map(), question: new Map() };
  const hit = metaCache.get(meta);
  if (hit && hit.network === network && hit.rules === rules) return hit.tags;
  const qIdx = questionIndex(meta);
  const outcome = new Map<number, string[]>();
  for (const o of meta.outcomes ?? []) outcome.set(o.outcome, classify(rules, recordForOutcome(network, o, qIdx.get(o.outcome))));
  const question = new Map<number, string[]>();
  for (const q of meta.questions ?? []) question.set(q.question, classify(rules, recordForQuestion(network, q)));
  const tags = { outcome, question };
  metaCache.set(meta, { network, rules, tags });
  return tags;
}

// ---- downloadable rules (#875 v2) ----------------------------------------------------------------------------------

/** The live rules file (shared/classify/tags.json, published by every web deploy, max-age=300). The web app fetches
 * the same file from its own origin, so dev servers and previews serve their own copy. */
export const RULES_URL = 'https://hip4.bitbazaar.cc/classify/tags.json';
export const RULES_PATH = 'classify/tags.json';
export const RULES_CACHE_KEY = 'outfox.classify.rules';
export const RULES_ATTEMPT_KEY = 'outfox.classify.rulesAttempt';
/** At most one download per hour (per device: the attempt time is stored). */
export const RULES_REFRESH_MS = 60 * 60 * 1000;

/** Parse + validate a rules file (JSON, schemaVersion, schema basics). Throws on any problem. */
export function parseRules(text: string): Rules {
  return loadRules(JSON.parse(text) as RulesData);
}

export interface RulesCache {
  read(): string | null;
  write(text: string): void;
  lastAttempt(): number;
  setLastAttempt(ms: number): void;
}

/** localStorage cache; every access may throw (private mode) and then acts as empty. */
export function storageRulesCache(store: Store | undefined = defaultStore()): RulesCache {
  const get = (k: string) => {
    try {
      return store?.getItem(k) ?? null;
    } catch {
      return null;
    }
  };
  const set = (k: string, v: string) => {
    try {
      store?.setItem(k, v);
    } catch {
      /* unavailable */
    }
  };
  return {
    read: () => get(RULES_CACHE_KEY),
    write: (text) => set(RULES_CACHE_KEY, text),
    lastAttempt: () => Number(get(RULES_ATTEMPT_KEY) ?? 0) || 0,
    setLastAttempt: (ms) => set(RULES_ATTEMPT_KEY, String(ms)),
  };
}

/**
 * The rules in use: the last good downloaded copy when it is valid and at least as new as the bundled one, else the
 * bundled copy. refreshIfDue() downloads at most once per RULES_REFRESH_MS; any failure (offline, bad JSON, schema,
 * unsupported schemaVersion) keeps what is in use.
 */
export class RulesStore {
  private cur: Rules;
  private readonly listeners = new Set<() => void>();
  private inflight: Promise<boolean> | null = null;

  constructor(
    private readonly bundled: Rules,
    private readonly cache: RulesCache,
    private readonly fetchText: () => Promise<string>,
    private readonly now: () => number = Date.now,
  ) {
    this.cur = bundled;
    const cached = this.cache.read();
    if (cached) {
      try {
        const r = parseRules(cached);
        if (r.version >= bundled.version) this.cur = r;
      } catch {
        /* bad cache: bundled */
      }
    }
  }

  get current(): Rules {
    return this.cur;
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): Rules => this.cur;

  /** Resolves true when a new copy was taken into use. */
  refreshIfDue(): Promise<boolean> {
    if (this.inflight) return this.inflight;
    const t = this.now();
    if (t - this.cache.lastAttempt() < RULES_REFRESH_MS) return Promise.resolve(false);
    this.cache.setLastAttempt(t);
    this.inflight = (async () => {
      try {
        const text = await this.fetchText();
        const r = parseRules(text);
        if (r.version < this.bundled.version) return false; // older than this build ships: ignore
        this.cache.write(text);
        if (r.version === this.cur.version) return false; // same rules already in use
        this.cur = r;
        for (const fn of this.listeners) fn();
        return true;
      } catch {
        return false;
      } finally {
        this.inflight = null;
      }
    })();
    return this.inflight;
  }
}

async function fetchRulesText(): Promise<string> {
  const base = (import.meta.env.BASE_URL as string | undefined) ?? '/';
  const res = await fetch(`${base}${RULES_PATH}`, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`rules: HTTP ${res.status}`);
  return res.text();
}

/** The app's rules store (bundled + localStorage cache + same-origin download). Never downloads under vitest. */
export const rulesStore = new RulesStore(
  RULES,
  storageRulesCache(),
  import.meta.env.MODE === 'test' ? () => Promise.reject(new Error('no download in unit tests')) : fetchRulesText,
);

// ---- remembered selection (per device) ------------------------------------------------------------------------------

/** The selected chips, as a JSON array of tag ids. */
export const TAGS_STORAGE_KEY = 'outfox.catalog.tags';
/** Before multi-select: one tag id. Read once as a one-element list, then removed. */
export const LEGACY_TAG_STORAGE_KEY = 'outfox.catalog.tag';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

let memory: string[] = [];

function defaultStore(): Store | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

/** Parse a ?tag= value or a stored list: comma-separated ids, blanks and duplicates dropped. */
export function parseTagList(raw: string | null | undefined): string[] {
  const out: string[] = [];
  for (const t of (raw ?? '').split(',')) {
    const id = t.trim();
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/** The stored selection ([] = All). Storage can be missing or throw (private mode): the in-memory copy then applies. */
export function loadStoredTags(store: Store | undefined = defaultStore()): string[] {
  try {
    if (store) {
      const raw = store.getItem(TAGS_STORAGE_KEY);
      if (raw !== null) {
        const v: unknown = JSON.parse(raw);
        return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
      }
      const legacy = store.getItem(LEGACY_TAG_STORAGE_KEY);
      if (legacy !== null) return parseTagList(legacy);
      return [];
    }
  } catch {
    /* fall through */
  }
  return memory;
}

export function saveStoredTags(tags: readonly string[], store: Store | undefined = defaultStore()): void {
  memory = [...tags];
  try {
    store?.removeItem(LEGACY_TAG_STORAGE_KEY);
    if (tags.length > 0) store?.setItem(TAGS_STORAGE_KEY, JSON.stringify(tags));
    else store?.removeItem(TAGS_STORAGE_KEY);
  } catch {
    /* storage unavailable: the in-memory copy still applies for this session */
  }
}

/** Test hook: forget the in-memory fallback. */
export function resetStoredTagMemory(): void {
  memory = [];
}
